import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmt } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import BotName from "@/components/BotName";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import { TrophyIcon, WarningIcon, RocketLaunchIcon, FlaskIcon, EyeIcon, CheckCircleIcon } from "@phosphor-icons/react";

const READINESS_META = {
  GO_LIVE: { label: "GO LIVE", color: "#2ECA7F", bg: "rgba(46,202,127,0.10)", border: "rgba(46,202,127,0.35)", icon: RocketLaunchIcon },
  PAPER_TRADE: { label: "PAPER TRADE", color: "#D6FF38", bg: "rgba(214,255,56,0.10)", border: "rgba(214,255,56,0.30)", icon: FlaskIcon },
  WATCH: { label: "WATCH", color: "#94A3B8", bg: "rgba(148,163,184,0.10)", border: "rgba(148,163,184,0.25)", icon: EyeIcon },
};

export default function Candidates() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/candidates").then(r => setData(r.data)).catch(console.error); }, []);

  if (!data) return <div className="p-6 mono text-sm text-[#64748B]">Scoring candidates...</div>;
  const { candidates, total } = data;

  const grouped = { GO_LIVE: [], PAPER_TRADE: [], WATCH: [] };
  for (const c of candidates) grouped[c.readiness]?.push(c);

  return (
    <div data-testid="candidates-page" className="p-4 md:p-6 space-y-5">
      <div>
        <div className="text-[10px] mono uppercase tracking-widest text-[#D6FF38]/70">Live-Deployment Shortlist</div>
        <h1 className="text-xl md:text-2xl tracking-tight font-medium mt-1 text-[#F1F5F9]">Deploy Candidates</h1>
        <p className="text-sm text-[#94A3B8] mt-2 max-w-2xl leading-relaxed">
          Bots that pass quant gates (10+ months tested, positive growth, PF &gt; 1.2, win-months &gt; 60%, drawdown &lt; 20%, not graveyard) — ranked by a weighted robustness score.
        </p>
      </div>

      {/* Gate summary tile */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {["GO_LIVE", "PAPER_TRADE", "WATCH"].map(k => {
          const m = READINESS_META[k];
          const Icon = m.icon;
          return (
            <div key={k} className="border rounded-md p-4" style={{ borderColor: m.border, background: m.bg }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon size={16} color={m.color} weight="duotone" />
                  <span className="text-[10px] mono uppercase tracking-widest" style={{ color: m.color }}>{m.label}</span>
                </div>
                <span className="mono text-2xl" style={{ color: m.color }}>{grouped[k].length}</span>
              </div>
              <div className="text-[11px] text-[#94A3B8] mt-1">
                {k === "GO_LIVE" && "score 70+ - deploy now"}
                {k === "PAPER_TRADE" && "score 55-69 - test on demo first"}
                {k === "WATCH" && "score under 55 - keep watching"}
              </div>
            </div>
          );
        })}
      </div>

      {total === 0 && (
        <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-8 text-center">
          <WarningIcon size={32} className="mx-auto text-[#94A3B8] mb-2" />
          <div className="text-sm text-[#F1F5F9]">No bots pass the gates yet</div>
          <div className="text-xs text-[#94A3B8] mt-1">Keep iterating — the AI brain will surface candidates as your fleet matures.</div>
        </div>
      )}

      {/* Candidates list */}
      <div className="space-y-3">
        {candidates.map((c, i) => {
          const m = READINESS_META[c.readiness];
          const Icon = m.icon;
          return (
            <div
              key={c.id || c.name}
              data-testid={`candidate-${i}`}
              className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-5 hover:border-[#3a3e4f] transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="text-[10px] mono text-[#64748B] w-6 pt-1">#{i + 1}</div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/bot/${c.id || c.name}`} className="mono text-lg text-[#F1F5F9] hover:text-[#D6FF38]">
                        <BotName name={c.name} maxLen={40} />
                      </Link>
                      <span className="text-[11px] mono text-[#94A3B8]">{c.symbol}</span>
                      <StatusBadge status={c.status} />
                      {c.suspicious && (
                        <span
                          data-testid={`suspicious-${i}`}
                          title="Zero drawdown with high growth — data quality suspect"
                          className="inline-flex items-center gap-1 text-[10px] mono uppercase tracking-widest px-2 py-0.5 rounded-sm border border-[#FF4D4D]/40 bg-[#FF4D4D]/10 text-[#FF4D4D]"
                        >
                          <WarningIcon size={10} weight="fill" /> Unverified
                        </span>
                      )}
                      <span
                        className="text-[10px] mono uppercase tracking-widest px-2 py-0.5 rounded-sm border inline-flex items-center gap-1"
                        style={{ color: m.color, background: m.bg, borderColor: m.border }}
                      >
                        <Icon size={10} weight="fill" />
                        {m.label}
                      </span>
                    </div>
                    {c.warnings && c.warnings.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {c.warnings.map((w, j) => (
                          <div key={j} className="text-[11px] text-[#FFB84D] flex items-center gap-1.5">
                            <WarningIcon size={11} weight="fill" /> {w}
                          </div>
                        ))}
                      </div>
                    )}
                    {c.warnings && c.warnings.length === 0 && (
                      <div className="mt-2 text-[11px] text-[#2ECA7F] flex items-center gap-1.5">
                        <CheckCircleIcon size={11} weight="fill" /> All quant gates pass cleanly
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">Deploy Score</div>
                  <div className="mono text-3xl mt-1" style={{ color: m.color }}>{c.deploy_score}</div>
                  <div className="text-[10px] mono text-[#94A3B8]">of 100</div>
                </div>
              </div>

              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-3 md:gap-4 mt-4 pt-4 border-t border-[#2A2D3A]">
                <Stat label="Growth" value={`${c.growth_pct >= 0 ? "+" : ""}${c.growth_pct.toFixed(1)}%`} tone={c.growth_pct >= 0 ? "profit" : "loss"} />
                <Stat label="Months" value={`${c.profitable_months}/${c.months_tested}`} />
                <Stat label="Win-Mo %" value={`${c.profit_month_pct}%`} />
                <Stat label="PF" value={c.profit_factor?.toFixed(2)} />
                <Stat label="Bal DD" value={`${(c.balance_dd ?? c.max_drawdown ?? 0)}%`} tone="loss" />
                <Stat label="Eq DD" value={`${c.equity_dd ?? 0}%`} tone="warn" />
                <Stat label="WR" value={`${(c.overall_win_rate ?? 0).toFixed(1)}%`} />
                <Stat label="Trades" value={c.total_trades ?? 0} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const color = tone === "profit" ? "#2ECA7F" : tone === "loss" ? "#FF4D4D" : tone === "warn" ? "#F97316" : "#F1F5F9";
  return (
    <div>
      <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">{label}</div>
      <div className="mono text-sm mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
